import React, {useEffect, useMemo, useState} from "react";
import {
  CancelButton,
  Input,
  SubmitContainer
} from "../../../assets/GlobalStyles";
import {useAtom} from "jotai";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {
  decimalFormat,
  isUnlimited,
  sortingAgentType
} from "../../../common/StringUtils";
import {
  deleteCampaign,
  retrieveAdverConfirm,
  retrieveConfirm,
  selAdverEnumInfo,
  updateCampaignDefaultInfo
} from "../../../services/campaign/ConfirmAxios";
import {tokenResultAtom} from "../../login/entity/Common";
import {selEnumInfo} from "../../../services/campaign/InfoAxios";
import {toast} from "react-toastify";
import {campaignBasicInfoAtom} from "../entity/Info";
import {stepCampaignAtom} from "../entity";
import {useResetAtom} from "jotai/utils";
import queryString from "query-string";
import {DeleteUser} from "../../../components/common/DeleteUser";
import styled from "styled-components";
import PlatformSectionHeader from "../../platform_manage/PlatformSectionHeader";
import SwitchComponent from "../../../components/switch";
import {
  updateAdverCampaignPublish,
  updateCampaignPublish
} from "../../../services/campaign/CreativeAxios";
import {selCampaignRegionTarget} from "../../../services/campaign/GroupAxios";

const EMPTY_TEXT = "-";

const CREATIVE_TYPE_LABEL = {
  AUDIO: "오디오",
  BANNER: "배너",
  LINK: "링크"
};

function CreativeTypeIcon({type}) {
  if (type === "BANNER") {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2"/>
          <circle cx="8" cy="9" r="1.5"/>
          <path d="m5.5 17 4.2-4.2 3 3 2.2-2.2 3.6 3.4"/>
        </svg>
    );
  }

  if (type === "LINK") {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9.5 14.5 14.5 9"/>
          <path d="M7.3 16.7 5.6 18.4a3.4 3.4 0 0 1-4.8-4.8l3.6-3.6a3.4 3.4 0 0 1 4.8 0"/>
          <path d="m16.7 7.3 1.7-1.7a3.4 3.4 0 0 1 4.8 4.8L19.6 14a3.4 3.4 0 0 1-4.8 0"/>
        </svg>
    );
  }

  return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9 18V6l10-2v12"/>
        <circle cx="6" cy="18" r="3"/>
        <circle cx="16" cy="16" r="3"/>
      </svg>
  );
}

export function CampaignLookOver() {
  const location = useLocation();
  const navigate = useNavigate();

  const [campaignBasicInfo] = useAtom(campaignBasicInfoAtom);
  const [, setStepCampaign] = useAtom(stepCampaignAtom);
  const [tokenUserInfo] = useAtom(tokenResultAtom);
  const resetBasicInfo = useResetAtom(campaignBasicInfoAtom);

  const [campaignData, setCampaignData] = useState(null);
  const [regionTarget, setRegionTarget] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [
    isEditingCampaignName,
    setIsEditingCampaignName
  ] = useState(false);
  const [userTargetConfig, setUserTargetConfig] = useState([]);
  const [audienceTargetConfig, setAudienceTargetConfig] = useState([]);
  const [agentTypeState, setAgentTypeState] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const TARGET_CONFIG_TYPE_LABEL = {
    INCLUDE: "선택 대상 포함",
    EXCLUDE: "선택 대상 제외",
    ALL: "전체",
    NONE: "미설정"
  };

  const GENDER_LABEL = {
    MALE: "남성",
    FEMALE: "여성"
  };

  const AGE_GROUP_LABEL = {
    AGE_10_19: "10대",
    AGE_20_29: "20대",
    AGE_30_39: "30대",
    AGE_40_49: "40대",
    AGE_50_59: "50대",
    AGE_60_PLUS: "60대 이상",
    UNKNOWN: "연령 미확인"
  };

  const getTargetConfigTypeLabel = type =>
      TARGET_CONFIG_TYPE_LABEL[type] ??
      type ??
      "미설정";

  const getGenderLabel = gender =>
      GENDER_LABEL[gender] ??
      gender ??
      EMPTY_TEXT;

  const getAgeGroupLabel = ageGroup => {
    if (!ageGroup) {
      return EMPTY_TEXT;
    }

    if (AGE_GROUP_LABEL[ageGroup]) {
      return AGE_GROUP_LABEL[ageGroup];
    }

    const matched = ageGroup.match(
        /^AGE_(\d+)$/
    );

    return matched
        ? `${matched[1]}대`
        : ageGroup;
  };
  const state = {
    ...(location.state ?? {}),
    ...(location.search !== ""
        ? queryString.parse(location.search)
        : {})
  };

  const campaignId =
      state?.campaignId ?? campaignBasicInfo?.campaignId;

  const isAdminView = tokenUserInfo?.role !== "NORMAL";
  const canEditCampaignName = isAdminView && Boolean(state?.campaignId);

  const handlePublishChange = async () => {
    const nextPublish = campaignData?.publishYn !== "Y";
    const result = await (
        isAdminView
            ? updateCampaignPublish(campaignId, nextPublish)
            : updateAdverCampaignPublish(campaignId, nextPublish)
    );

    if (!result) {
      toast.error("게재 상태를 변경하지 못했습니다.", {
        toastId: "campaignDetailPublishError"
      });
      return false;
    }

    setCampaignData(prev => ({
      ...prev,
      publishYn: nextPublish ? "Y" : "N"
    }));
    return true;
  };

  const inventoryExposure = inventoryDetail => {
    if (!inventoryDetail) {
      setUserTargetConfig([]);
      setAudienceTargetConfig([]);
      return;
    }

    setUserTargetConfig([
      inventoryDetail.exposureConversionUserYn === "Y"
          ? "전환 고객 노출"
          : `전환 고객 미노출[${inventoryDetail.nonExposureDaysOfConversionUser ?? 0}일]`,
      inventoryDetail.exposureShoppingUserYn === "Y"
          ? "쇼핑 고객 노출"
          : "쇼핑 고객 미노출",
      inventoryDetail.exposureAttentionUserYn === "Y"
          ? "관심 고객 노출"
          : "관심 고객 미노출",
      inventoryDetail.exposureVisitUserYn === "Y"
          ? "방문 고객 노출"
          : "방문 고객 미노출"
    ]);

    setAudienceTargetConfig([
      inventoryDetail.exposureConversionAudienceYn === "Y"
          ? "전환 유저 노출"
          : `전환 유저 미노출[${inventoryDetail.nonExposureDaysOfConversionAudience ?? 0}일]`,
      inventoryDetail.exposureShoppingAudienceYn === "Y"
          ? "쇼핑 유저 노출"
          : "쇼핑 유저 미노출",
      inventoryDetail.exposurePotentialAudienceYn === "Y"
          ? "잠재 유저 노출"
          : "잠재 유저 미노출",
      inventoryDetail.exposureNewAudienceYn === "Y"
          ? "신규 유저 노출"
          : "신규 유저 미노출"
    ]);
  };

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      if (!campaignId) {
        toast.error("캠페인 정보를 확인할 수 없습니다.", {
          toastId: "campaignLookOverIdMissing"
        });
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const [enumResponse, campaignResponse, regionResponse] = await Promise.all([
          isAdminView
              ? selEnumInfo("AGENT_TYPE")
              : selAdverEnumInfo("AGENT_TYPE"),
          isAdminView
              ? retrieveConfirm(campaignId)
              : retrieveAdverConfirm(campaignId),
          isAdminView
              ? selCampaignRegionTarget(campaignId)
              : Promise.resolve(null)
        ]);
        setCampaignName(
            campaignResponse.name ?? ""
        );

        setIsEditingCampaignName(false);
        if (!mounted) {
          return;
        }

        setAgentTypeState(
            isAdminView
                ? enumResponse?.values ?? []
                : enumResponse?.data ?? []
        );

        if (!campaignResponse) {
          throw new Error("캠페인 조회 응답이 없습니다.");
        }

        setCampaignData(campaignResponse);
        setRegionTarget(regionResponse || null);
        setCampaignName(campaignResponse.name ?? "");
        inventoryExposure(campaignResponse.inventoryDetail);
      } catch (error) {
        console.error("캠페인 검토 정보 조회 실패:", error);
        toast.error("캠페인 정보를 불러오는 중 오류가 발생했습니다.", {
          toastId: "campaignLookOverLoadError"
        });
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const handleChangeName = event => {
    setCampaignName(event.target.value);
  };

  const onCancel = () => {
    if (state) {
      navigate(-1);
      return;
    }

    resetBasicInfo();
    setStepCampaign({steps: 0});
  };

  const handleDelYn = async delYn => {
    if (delYn !== "Y" || !campaignId || isDeleting) {
      return;
    }

    const confirmed = window.confirm(
        `정말로 캠페인 "${campaignData?.name ?? campaignName}"을(를) 영구 삭제하시겠습니까?\n\n삭제된 캠페인과 관련 설정은 복구할 수 없습니다.`
    );

    if (!confirmed) {
      return;
    }

    setIsDeleting(true);

    try {
      const response = await deleteCampaign(campaignId, campaignName);

      if (!response) {
        throw new Error("캠페인 삭제 실패");
      }

      toast.success("캠페인이 삭제되었습니다.", {
        toastId: "deleteCampaignSuccess",
        autoClose: 800
      });

      window.setTimeout(() => {
        navigate(-1);
      }, 500);
    } catch (error) {
      console.error("캠페인 삭제 실패:", error);
      toast.error("캠페인 삭제 중 오류가 발생했습니다.", {
        toastId: "deleteCampaignError"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStartEditCampaignName = () => {
    setCampaignName(
        campaignData?.name ?? ""
    );

    setIsEditingCampaignName(true);
  };

  const handleCancelEditCampaignName = () => {
    setCampaignName(
        campaignData?.name ?? ""
    );

    setIsEditingCampaignName(false);
  };

  const handleSaveCampaignName = async () => {
    if (isSaving) {
      return;
    }

    const trimmedName =
        campaignName.trim();

    if (!trimmedName) {
      toast.warning(
          "캠페인명을 입력해주세요.",
          {
            toastId:
                "campaignNameRequired"
          }
      );

      return;
    }

    if (
        trimmedName ===
        (campaignData?.name ?? "").trim()
    ) {
      toast.info(
          "변경된 캠페인명이 없습니다.",
          {
            toastId:
                "campaignNameNotChanged"
          }
      );

      return;
    }

    if (!campaignId) {
      toast.error(
          "캠페인 정보를 확인할 수 없습니다.",
          {
            toastId:
                "campaignIdMissing"
          }
      );

      return;
    }

    setIsSaving(true);

    try {
      const response =
          await updateCampaignDefaultInfo(
              campaignId,
              trimmedName
          );

      if (!response) {
        throw new Error(
            "캠페인명 수정 실패"
        );
      }

      setCampaignData(prev => ({
        ...prev,
        name: trimmedName
      }));

      setCampaignName(trimmedName);
      setIsEditingCampaignName(false);

      toast.success(
          "캠페인명이 수정되었습니다.",
          {
            autoClose: 1000,
            toastId:
                "updateCampaign"
          }
      );
    } catch (error) {
      console.error(
          "캠페인명 수정 실패:",
          error
      );

      toast.error(
          "캠페인명 수정 중 오류가 발생했습니다.",
          {
            toastId:
                "updateCampaignError"
          }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const agentTypeText = useMemo(() => {
    const inventory = campaignData?.inventoryDetail;

    if (!inventory) {
      return EMPTY_TEXT;
    }

    const exposureAgentType = inventory.exposureAgentType ?? [];

    if (exposureAgentType.length === agentTypeState.length) {
      return "전체";
    }

    return sortingAgentType(exposureAgentType)
    .map(item => item.label)
    .join(", ");
  }, [campaignData, agentTypeState]);

  const inventoryTypeText = useMemo(() => {
    const inventory = campaignData?.inventoryDetail;

    if (!inventory) {
      return EMPTY_TEXT;
    }

    if (inventory.exposureInventoryType === "AUTO") {
      return "자동 최적화";
    }

    if (inventory.exposureInventoryType === "MANUAL") {
      return `${inventory.allowInventoryIds?.length ?? 0}개 지면 게재 설정`;
    }

    return "카테고리 설정";
  }, [campaignData]);

  const dailyBudgetText =
      campaignData?.infiniteBudgetYn !== "N"
          ? "무제한"
          : `${decimalFormat(campaignData?.dailyAvgBudget ?? 0)}원`;

  const budgetRatioText =
      campaignData?.infiniteBudgetYn !== "N"
          ? "무제한"
          : `PC ${decimalFormat(campaignData?.pcBudget ?? 0)}원 / MOBILE ${decimalFormat(campaignData?.mobBudget ?? 0)}원`;
  const rawCreativeType = String(
      campaignData?.creativeType ?? campaignData?.productType ?? "AUDIO"
  ).toUpperCase();
  const creativeDisplayType = rawCreativeType === "POP_UNDER"
      ? "LINK"
      : ["AUDIO", "BANNER", "LINK"].includes(rawCreativeType)
          ? rawCreativeType
          : "AUDIO";
  const isAudioCreative = creativeDisplayType === "AUDIO";
  const isBannerCreative = creativeDisplayType === "BANNER";
  const showsCreativeFile = isAudioCreative || isBannerCreative;
  const showsCreativeLanding = !isAudioCreative;
  const demographicTarget =
      campaignData?.demographicTarget;

  const genderTargets =
      demographicTarget?.genders?.map(
          getGenderLabel
      ) ?? [];

  const ageTargets =
      demographicTarget?.ageGroups?.map(
          getAgeGroupLabel
      ) ?? [];

  const regionTargets =
      regionTarget?.regionTargetConfigType === "MANUAL"
          ? (regionTarget?.regions ?? []).map(region =>
              [region.countryName, region.cityName, region.regionName]
              .filter(Boolean)
              .join(" ") || region.regionCode
          )
          : [];

  const advertiserInfoText =
      state?.adverInfo ??
      campaignData?.adverInfo ??
      campaignBasicInfo?.adverInfo ??
      "";
  const advertiserInfoMatch =
      String(advertiserInfoText).match(/^(.*)\(([^()]*)\)$/);
  const advertiserName =
      campaignData?.adverName ??
      campaignData?.advertiserName ??
      campaignBasicInfo?.adverName ??
      tokenUserInfo?.adverName ??
      advertiserInfoMatch?.[1]?.trim() ??
      advertiserInfoText ??
      EMPTY_TEXT;
  const advertiserUsername =
      campaignData?.username ??
      campaignData?.userName ??
      campaignBasicInfo?.username ??
      tokenUserInfo?.username ??
      advertiserInfoMatch?.[2]?.trim() ??
      campaignData?.userId ??
      state?.userId ??
      EMPTY_TEXT;

  if (isLoading) {
    return (
        <LoadingArea>캠페인 정보를 불러오고 있습니다.</LoadingArea>
    );
  }

  return (
      <>
        {campaignData && (
              <ReviewContent>
                <ReviewHero>
                  <ReviewHeroText>
                    {/*<ReviewEyebrow>FINAL REVIEW</ReviewEyebrow>*/}
                    <ReviewTitle>캠페인 설정을 최종 확인해 주세요</ReviewTitle>
                    <ReviewDescription>
                      기본 정보부터 소재까지 주요 설정을 한 화면에서 확인할 수 있습니다.
                    </ReviewDescription>
                  </ReviewHeroText>

                  <ReviewMetrics>
                    <ReviewMetric>
                      <MetricLabel>광고주 정보</MetricLabel>
                      <MetricValue title={`${advertiserName} (${advertiserUsername})`}>
                        {advertiserName || EMPTY_TEXT}
                      </MetricValue>
                      {/*<MetricSubValue title={String(advertiserUsername)}>*/}
                      {/*  {advertiserUsername || EMPTY_TEXT}*/}
                      {/*</MetricSubValue>*/}
                    </ReviewMetric>
                    <ReviewMetric>
                      <MetricLabel>캠페인 상품</MetricLabel>
                      <MetricValue>{campaignData.productType ?? EMPTY_TEXT}</MetricValue>
                    </ReviewMetric>
                    <ReviewMetric>
                      <MetricLabel>일 예산</MetricLabel>
                      <MetricValue $accent={dailyBudgetText === "무제한"}>{dailyBudgetText}</MetricValue>
                    </ReviewMetric>
                    <ReviewMetric>
                      <MetricLabel>지면 설정</MetricLabel>
                      {/*<MetricValue>{campaignData.inventoryDetail ? "설정 완료" : "미설정"}</MetricValue>*/}
                      <MetricValue>
                        <CreativeStatus $ready={Boolean(campaignData.inventoryDetail)}>{campaignData.inventoryDetail ? "등록" : "미등록"}</CreativeStatus>
                      </MetricValue>
                    </ReviewMetric>

                    <ReviewMetric>
                      <MetricLabel>소재</MetricLabel>
                      <MetricValue>
                        <CreativeStatus $ready={Boolean(campaignData.creativeType)}>{campaignData.creativeType || campaignData.filePath ? "등록" : "미등록"}</CreativeStatus>
                      </MetricValue>
                      {/*<MetricValue>{campaignData.creativeType || campaignData.filePath ? "등록 완료" : "미등록"}</MetricValue>*/}
                    </ReviewMetric>

                  </ReviewMetrics>
                </ReviewHero>

                <BasicSettingCard>
                  <SectionHeader>
                    <SectionTitle>캠페인 기본 정보</SectionTitle>
                    <SectionHeaderActions>
                      {/*<SectionGuide>캠페인의 주요 설정 정보를 최종 확인합니다.</SectionGuide>*/}
                      {/*<ReviewMetric>*/}
                      {/*  <MetricLabel>캠페인 게재 여부 </MetricLabel>*/}
                        <SwitchComponent
                            value={campaignData.publishYn === "Y"}
                            type="publish"
                            cellProps={{data: campaignData}}
                            eventClick={handlePublishChange}
                        />
                      {/*</ReviewMetric>*/}
                    </SectionHeaderActions>
                  </SectionHeader>
                    <InnerBox>
                      <InfoGrid>
                        <InfoCard>
                          <InfoLabel>캠페인 명</InfoLabel>
                          <InfoValue style={{display: 'flex'}}>
                            {canEditCampaignName &&
                            isEditingCampaignName ? (
                                <CampaignNameEditArea>
                                  <BasicInfoInput
                                      type="text"
                                      maxLength={100}
                                      value={campaignName}
                                      disabled={isSaving}
                                      placeholder="캠페인명을 입력해주세요."
                                      onChange={handleChangeName}
                                      onKeyDown={event => {
                                        if (event.key === "Enter") {
                                          event.preventDefault();
                                          handleSaveCampaignName();
                                        }

                                        if (event.key === "Escape") {
                                          handleCancelEditCampaignName();
                                        }
                                      }}
                                  />

                                  <CampaignNameActions>
                                    <SaveNameButton
                                        type="button"
                                        disabled={isSaving}
                                        onClick={
                                          handleSaveCampaignName
                                        }
                                    >
                                      {isSaving
                                          ? "처리 중"
                                          : "저장"}
                                    </SaveNameButton>

                                    <CancelEditButton
                                        type="button"
                                        disabled={isSaving}
                                        onClick={
                                          handleCancelEditCampaignName
                                        }
                                    >
                                      취소
                                    </CancelEditButton>
                                  </CampaignNameActions>
                                </CampaignNameEditArea>
                            ) : (
                                <>
                                  <BasicInfoValue>
                                    {campaignData.name ??
                                        EMPTY_TEXT}
                                  </BasicInfoValue>
                                  {canEditCampaignName && (
                                      <EditNameButton
                                          type="button"
                                          disabled={isSaving}
                                          onClick={handleStartEditCampaignName}
                                      >
                                        수정하기
                                      </EditNameButton>
                                  )}
                                </>
                            )}
                          </InfoValue>
                        </InfoCard>
                        <InfoCard>
                          <InfoLabel>캠페인 상품</InfoLabel>
                          <InfoValue style={{display:'flex', justifyContent: 'end'}}>
                          <CreativeIcon $type={creativeDisplayType}>
                            <CreativeTypeIcon type={creativeDisplayType}/>
                          </CreativeIcon>
                            <span>{campaignData.productType ?? EMPTY_TEXT}</span>
                          </InfoValue>
                        </InfoCard>
                      </InfoGrid>
                    </InnerBox>
                </BasicSettingCard>

                <SettingCard>
                  <SectionHeader>
                    <SectionTitle>예산 및 입찰 설정</SectionTitle>

                    <DetailArrow
                        to={{
                          pathname: "/board/campaignTwo",
                          search: `campaignId=${campaignId}&userId=${campaignData.userId || state?.userId || ""}&adverInfo=${state?.adverInfo ?? ""}`
                        }}
                        state={{
                          campaignId,
                          userId: campaignData.userId || state?.userId || "",
                          adverInfo: state?.adverInfo ?? ""
                        }}
                    >수정 ›</DetailArrow>
                  </SectionHeader>

                  <InnerBox>
                    <InfoGrid>
                      <InfoCard>
                        <InfoLabel>일일 평균 예산</InfoLabel>
                        <InfoValue $accent={dailyBudgetText === "무제한"}>
                          {dailyBudgetText}
                        </InfoValue>
                      </InfoCard>

                      {dailyBudgetText !== "무제한" &&
                      <InfoCard>
                        <InfoLabel>예산 비율</InfoLabel>
                        <InfoValue>{budgetRatioText}</InfoValue>
                      </InfoCard>
                      }

                      <InfoCard>
                        <InfoLabel>시간별 예산 그룹</InfoLabel>
                        <InfoValue>
                          {campaignData?.budgetTimeName ?? EMPTY_TEXT}
                        </InfoValue>
                      </InfoCard>

                      <InfoCard>
                        <InfoLabel>입찰 방식</InfoLabel>
                        <InfoValue>
                          {campaignData.biddingType ?? EMPTY_TEXT}
                        </InfoValue>
                      </InfoCard>

                      <InfoCard>
                        <InfoLabel>입찰 단가</InfoLabel>
                        <InfoValue>
                          {campaignData?.biddingPrice != null
                              ? `${decimalFormat(campaignData.biddingPrice)}원`
                              : EMPTY_TEXT}
                        </InfoValue>
                      </InfoCard>
                    </InfoGrid>
                  </InnerBox>
                </SettingCard>

                <SettingCard>
                  <SectionHeader>
                    <SectionTitle>광고 그룹 설정</SectionTitle>
                    <DetailArrow
                        to={{pathname: "/board/campaignThree", search: `campaignId=${campaignId}&adverInfo=${state?.adverInfo ?? ""}`}}
                        state={{campaignId, adverInfo: state?.adverInfo}}
                    >수정 ›</DetailArrow>
                  </SectionHeader>

                  <InnerBox>
                    {campaignData.inventoryDetail ? (
                        <>
                          <InfoGrid>
                            {/*<InfoCard>*/}
                            {/*  <InfoLabel>노출 영역</InfoLabel>*/}
                            {/*  <InfoValue>{agentTypeText}</InfoValue>*/}
                            {/*</InfoCard>*/}

                            <InfoCard>
                              <InfoLabel>게재 지면</InfoLabel>
                              <InfoValue>{inventoryTypeText}</InfoValue>
                            </InfoCard>

                            <InfoCard>
                              <InfoLabel>게재 기간</InfoLabel>
                              <InfoValue>
                                {campaignData.inventoryDetail?.startDate} ~ {isUnlimited(
                                  campaignData.inventoryDetail?.endDate
                              )}
                              </InfoValue>
                            </InfoCard>
                          </InfoGrid>
                        </>
                    ) : (
                        <EmptySetting>
                          <EmptyDescription>
                            광고 게재 설정이 등록되지 않았습니다.
                          </EmptyDescription>

                          <SettingLink
                              to={{
                                pathname: "/board/campaignThree",
                                search: `campaignId=${campaignId}&adverInfo=${state?.adverInfo ?? ""}`
                              }}
                              state={{
                                campaignId,
                                adverInfo: state?.adverInfo
                              }}
                          >
                            광고 게재 설정
                          </SettingLink>
                        </EmptySetting>
                    )}
                    <DemographicSection>

                      {demographicTarget ? (
                          <TargetArea>
                            <TargetGroup>
                              <TargetTitleRow>
                                <TargetTitle>
                                  성별 타겟팅
                                </TargetTitle>
                              </TargetTitleRow>
                              <TargetTagsRow>
                                <TargetTags>
                                  {genderTargets.length > 0 ? (
                                      genderTargets.map(
                                          gender => (
                                              <TargetTag
                                                  key={gender}
                                              >
                                                {gender}
                                              </TargetTag>
                                          )
                                      )
                                  ) : (
                                      <EmptyTargetText>
                                        전체
                                      </EmptyTargetText>
                                  )}
                                </TargetTags>
                            </TargetTagsRow>
                            </TargetGroup>

                            <TargetGroup>
                              <TargetTitleRow>
                                <TargetTitle>
                                  연령 타겟팅
                                </TargetTitle>
                              </TargetTitleRow>

                              <TargetTagsRow>
                              <TargetTags>
                                {ageTargets.length > 0 ? (
                                    ageTargets.map(
                                        age => (
                                            <TargetTag
                                                key={age}
                                            >
                                              {age}
                                            </TargetTag>
                                        )
                                    )
                                ) : (
                                    <EmptyTargetText>
                                      전체
                                    </EmptyTargetText>
                                )}
                              </TargetTags>
                              </TargetTagsRow>
                            </TargetGroup>

                            <TargetGroup>
                              <TargetTitleRow>
                                <TargetTitle>
                                  지역 타겟팅
                                </TargetTitle>
                              </TargetTitleRow>

                              <TargetTagsRow>
                              <TargetTags>
                                {regionTarget?.regionTargetConfigType === "MANUAL" ? (
                                    regionTargets.length > 0 ? (
                                        regionTargets.map((region, index) => (
                                            <TargetTag key={`${region}-${index}`}>
                                              {region}
                                            </TargetTag>
                                        ))
                                    ) : (
                                        <EmptyTargetText>미설정</EmptyTargetText>
                                    )
                                ) : (
                                    <EmptyTargetText>전체</EmptyTargetText>
                                )}
                              </TargetTags>
                              </TargetTagsRow>
                            </TargetGroup>
                          </TargetArea>
                      ) : (
                          <EmptyDemographicTarget>
                            성별/연령/지역 정보가 설정되지 않았습니다.
                          </EmptyDemographicTarget>
                      )}
                    </DemographicSection>
                  </InnerBox>
                </SettingCard>

                <SettingCard>
                  <SectionHeader>
                    <SectionTitle>소재 설정</SectionTitle>
                    <DetailArrow
                        to={{pathname: "/board/campaignFour", search: `campaignId=${campaignId}&creativeType=${campaignData.creativeType ?? ""}&productType=${campaignData.productType ?? ""}&adverInfo=${state?.adverInfo ?? ""}`}}
                        state={{campaignId, creativeType: campaignData.creativeType, productType: campaignData.productType, adverInfo: state?.adverInfo}}
                    >수정 ›</DetailArrow>
                  </SectionHeader>

                  <InnerBox>
                    <CreativeGrid>
                      <InfoCard>
                        <InfoLabel>소재 유형</InfoLabel>
                        <CreativeTypeValue style={{display:'flex', justifyContent: 'end'}}>
                          <CreativeIcon $type={creativeDisplayType}>
                            <CreativeTypeIcon type={creativeDisplayType}/>
                          </CreativeIcon>
                          <span>{CREATIVE_TYPE_LABEL[creativeDisplayType]}</span>
                        </CreativeTypeValue>
                      </InfoCard>
                      {showsCreativeFile && (
                          <InfoCard>
                            <InfoLabel>소재 명</InfoLabel>
                            <InfoValue>{campaignData.creativeName ?? campaignData.filePath ?? "미등록"}</InfoValue>
                          </InfoCard>
                      )}
                      {isAudioCreative && (
                          <InfoCard $wide>
                            <InfoLabel>보조 이미지</InfoLabel>
                            <InfoValue $accent={Boolean(campaignData.companionImagePath)}>
                              {campaignData.companionImagePath ? "" : "미등록"}
                            </InfoValue>
                            {isAudioCreative && campaignData.companionImagePath && (
                                <CompanionReviewCard>
                                  <CompanionReviewImage
                                      src={campaignData.companionImagePath}
                                      alt="오디오 보조 이미지"
                                  />
                                  <CompanionReviewInfo>
                                    <InfoCard>
                                      <InfoLabel>보조 이미지 정보</InfoLabel>
                                      <InfoValue>
                                        {[campaignData.companionImageWidth, campaignData.companionImageHeight]
                                            .filter(value => value != null)
                                            .join(" × ") || "크기 정보 없음"}
                                        {campaignData.companionImageMimeType
                                            ? ` · ${campaignData.companionImageMimeType}`
                                            : ""}
                                      </InfoValue>
                                    <InfoLabel>보조 이미지 랜딩 URL</InfoLabel>
                                      <InfoValue>
                                      {campaignData.companionLandingUrl ? (
                                          <CompanionLandingLink
                                              href={campaignData.companionLandingUrl}
                                              target="_blank"
                                              rel="noreferrer"
                                          >
                                            {campaignData.companionLandingUrl}
                                          </CompanionLandingLink>
                                      ) : (
                                          <InfoValue>미등록</InfoValue>
                                      )}
                                      </InfoValue>
                                </InfoCard>
                                  </CompanionReviewInfo>
                                </CompanionReviewCard>
                            )}
                          </InfoCard>
                      )}

                      {showsCreativeLanding && (
                          <InfoCard>
                            <InfoLabel>PC 랜딩 URL</InfoLabel>
                            <InfoValue>{campaignData.pcLandingUrl ?? "미등록"}</InfoValue>
                          </InfoCard>
                      )}
                      {showsCreativeLanding && (
                          <InfoCard>
                            <InfoLabel>모바일 랜딩 URL</InfoLabel>
                            <InfoValue>{campaignData.mobLandingUrl ?? "미등록"}</InfoValue>
                          </InfoCard>
                      )}
                    </CreativeGrid>
                  </InnerBox>
                </SettingCard>
              </ReviewContent>
        )}

        {tokenUserInfo?.role === "SUPER_ADMIN" && (
            <DeleteSectionCard>
              <PlatformSectionHeader
                  title="캠페인 삭제"
                  description="삭제한 캠페인과 연결된 설정은 복구할 수 없으므로 신중하게 진행해 주세요."
                  collapsible
                  open={isDeleteOpen}
                  onToggle={() => setIsDeleteOpen(open => !open)}
              />
              {isDeleteOpen && <DeleteSectionBody>
                <DeleteCard>
                  <DeleteWarningIcon aria-hidden="true">!</DeleteWarningIcon>
                  <DeleteWarningContent>
                    <DeleteWarningHeadline>
                      삭제 후 복구할 수 없습니다
                    </DeleteWarningHeadline>
                    <DeleteWarningText>
                      캠페인과 연결된 예산·타겟팅·소재 설정이 함께 삭제됩니다. 삭제할 캠페인명을 다시 확인해 주세요.
                    </DeleteWarningText>
                    <DeleteCampaignName>
                      삭제 대상: {campaignData?.name ?? campaignName ?? EMPTY_TEXT}
                    </DeleteCampaignName>
                  </DeleteWarningContent>
                  <DeleteActionArea>
                    <DeleteDescription>
                      캠페인명과 연결된 설정을 확인한 후 진행해 주세요.
                    </DeleteDescription>
                    <DeleteUser
                        title="캠페인 삭제"
                        buttonText="삭제하기"
                        onSubmit={handleDelYn}
                        btnStyle="SearchUser"
                        disabled={isDeleting}
                    />
                  </DeleteActionArea>
                </DeleteCard>
              </DeleteSectionBody>}
            </DeleteSectionCard>
        )}

        <ReviewActions>
          <ReviewActionGuide>
            <ReviewActionTitle>캠페인 검토를 마쳤습니다.</ReviewActionTitle>
            <ReviewActionDescription>
              검토 내용을 확인한 뒤 캠페인 목록으로 이동할 수 있습니다.
            </ReviewActionDescription>
          </ReviewActionGuide>
          <CancelButton
              type="button"
              disabled={isSaving || isDeleting}
              onClick={onCancel}
          >
            {isAdminView && state ? "목록" : "확인"}
          </CancelButton>

        </ReviewActions>
      </>
  );
}

const LoadingArea = styled.div`
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: var(--font-size-15);
`;

const DeleteSectionCard = styled.section`
  overflow: hidden;
  margin: 24px 0;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, 0.045);

  @media (max-width: 980px) {
    margin-right: 18px;
    margin-left: 18px;
  }
`;

const DeleteSectionBody = styled.div`
  padding: 18px 20px 20px;
  background: #fff;
`;

const ReviewContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin: 0;
  background: #f5f7f9;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    padding: 18px;
  }
`;

const ReviewHero = styled.section`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: minmax(230px, 0.8fr) minmax(0, 1.6fr);
  gap: 18px;
  align-items: center;
  padding: 18px;
  border: 1px solid #f0dfd1;
  border-radius: 10px;
  background: linear-gradient(135deg, #fff8f2 0%, #fff 65%);
  box-shadow: 2px 2px 8px -5px rgba(31, 41, 55, 1);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ReviewHeroText = styled.div``;

const ReviewEyebrow = styled.div`
  margin-bottom: 5px;
  color: #f5811f;
  font-size: var(--font-size-11);
  font-weight: 800;
  letter-spacing: 0.14em;
`;

const ReviewTitle = styled.h2`
  margin: 0;
  color: #242a31;
  font-size: var(--font-size-19);
  line-height: 1.35;
  letter-spacing: -0.03em;
`;

const ReviewDescription = styled.p`
  margin-top: 6px;
  color: #737c85;
  font-size: var(--font-size-12);
  line-height: 1.55;
`;

const ReviewMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 9px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ReviewMetric = styled.div`
  min-width: 0;
  padding: 10px;
  border: 1px solid #eceff2;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.88);
`;

const MetricLabel = styled.div`
  margin-bottom: 4px;
  color: #818991;
  font-size: var(--font-size-11);
`;

const MetricValue = styled.div`
  overflow: hidden;
  color: ${({$accent}) => ($accent ? "#f5811f" : "#30363d")};
  font-size: var(--font-size-13);
  text-align: end;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CreativeStatus = styled.span`
  display: inline-flex;
  min-height: 22px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 8px;
  background: ${({$ready}) => $ready ? "#eaf8f0" : "#f2f3f4"};
  color: ${({$ready}) => $ready ? "#258652" : "#7c8389"};
  font-size: var(--font-size-label);
  font-weight: 800;
`;

const MetricSubValue = styled.div`
  overflow: hidden;
  margin-top: 3px;
  text-align: right;
  color: #8a9299;
  font-size: var(--font-size-10);
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SettingCard = styled.div`
  min-width: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
  border: 1px solid #e4e8ec;
  border-radius: 10px;
  background: #fff;
  box-shadow: 2px 2px 8px -5px rgba(31, 41, 55, 1);
`;

const SectionHeader = styled.div`
  display: flex;
  min-height: 56px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 0;
  padding: 11px 16px;
  border-bottom: 1px solid #e3e7ea;
  background: linear-gradient(135deg, rgb(255, 255, 255) 0%, rgb(255, 248, 241) 100%)
`;

const BasicSettingCard = styled(SettingCard)`
  display: flex;
  flex-direction: column;

  > div:last-child {
    flex: 1;
    align-content: start;
    //background-color: #f5f7f9;
  }
`;

const SectionTitle = styled.div`
  color: #222;
  font-size: var(--font-size-body-large);
  font-weight: 700;
`;

const SectionGuide = styled.span`
  color: #888;
  font-size: var(--font-size-caption);
  line-height: 1.5;
  text-align: right;
`;

const ReviewActions = styled(SubmitContainer)`
  position: sticky;
  bottom: 0;
  z-index: 5;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 22px;
  padding: 16px 20px;
  border: 1px solid #ffd7b5;
  border-left: 4px solid #f5811f;
  border-radius: 10px;
  background: linear-gradient(90deg, #fff8f2 0%, rgba(255, 255, 255, 0.98) 48%);
  box-shadow: 0 -4px 20px rgba(245, 129, 31, 0.09);

  button {
    width: auto;
    min-width: 108px;
    height: 40px;
    margin: 0;
    padding: 0 22px;
    border-color: #f5811f;
    border-radius: 6px;
    background: #f5811f;
    color: #fff;
    font-size: var(--font-size-13);
    font-weight: 700;
    box-shadow: 0 5px 12px rgba(245, 129, 31, 0.2);

    &:hover:not(:disabled) {
      border-color: #e9700d;
      background: #e9700d;
    }
  }

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

const ReviewActionGuide = styled.div`
  min-width: 0;
  text-align: left;
`;

const ReviewActionTitle = styled.strong`
  display: block;
  color: #2f3438;
  font-size: var(--font-size-14);
  font-weight: 800;
`;

const ReviewActionDescription = styled.span`
  display: block;
  margin-top: 4px;
  color: #7f878e;
  font-size: var(--font-size-12);
  line-height: 1.45;
`;

const InnerBox = styled.div`
  min-height: calc(100% - 56px);
  padding: 14px;
  //background: #f5f7f9;
`;

const SectionHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const sectionEditStyle = `
  display: inline-flex;
  min-width: 48px;
  min-height: 28px;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border: 1px solid #f1d4c2;
  border-radius: 999px;
  background: #fff8f1;
  color: #d96500;
  font-size: var(--font-size-12);
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    border-color: #f5811f;
    background: #fff1e4;
  }
`;

const DetailArrow = styled(Link)`
  color: #d96500;
  font-size: var(--font-size-micro);
  font-weight: 700;
`;

const SectionEditLink = styled(Link)`
  ${sectionEditStyle}
`;

const SectionEditButton = styled.button`
  ${sectionEditStyle}
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const FieldLabel = styled.div`
  width: 140px;
  min-width: 140px;
  padding-top: 10px;
  color: #444;
  font-size: var(--font-size-16);
  font-weight: 600;
`;

const FieldContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 10px;
`;

const FieldValue = styled.div`
  display: flex;
  min-height: 40px;
  align-items: center;
  color: #333;
  font-size: var(--font-size-16);
  font-weight: 500;
`;

const CampaignNameInput = styled(Input)`
  width: 100%;
  min-width: 340px;
  max-width: 460px;
  height: 40px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;

  &:focus {
    border-color: #f5811f;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #999;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const CreativeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
const InfoCard = styled.div`
  grid-column: ${({$wide}) => ($wide ? "1 / -1" : "auto")};
  min-height: 68px;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;
`;

const InfoLabel = styled.div`
  margin-bottom: 6px;
  color: #888;
  font-size: var(--font-size-12);
`;

const InfoValue = styled.div`
  display: block;
  text-align: right;
  -webkit-box-align: center;
  align-items: center;
  gap: 10px;
  color: ${({$accent}) => ($accent ? "#f5811f" : "#333")};
  font-size: var(--font-size-body);
  font-weight: 600;
  line-height: 1.5;
  word-break: keep-all;
`;

const CompanionReviewCard = styled.div`
  display: flex;
  grid-column: 1 / -1;
  align-items: center;
  gap: 16px;
  padding: 14px;
  border: 1px solid #f1d4c2;
  border-radius: 8px;
  background: #fffaf6;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const CompanionReviewImage = styled.img`
  width: 140px;
  height: 100px;
  flex: 0 0 140px;
  object-fit: contain;
  border: 1px solid #e5e5e5;
  border-radius: 7px;
  background: #fff;
`;

const CompanionReviewInfo = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 5px;
`;

const CompanionLandingLink = styled.a`
  overflow: hidden;
  color: #d96500;
  font-size: var(--font-size-13);
  font-weight: 600;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const TargetArea = styled.div`
  display: grid;
  grid-template-columns:
    repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TargetGroup = styled.div`
  grid-column: ${({$wide}) => ($wide ? "1 / -1" : "auto")};
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;
`;

const TargetTitle = styled.div`
  margin-bottom: 9px;
  color: #888;
  font-size: var(--font-size-caption);
`;

const TargetTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const TargetTag = styled.span`
  display: inline-flex;
  min-height: 26px;
  align-items: center;
  padding: 0 9px;
  border: 1px solid #f5811f;
  border-radius: 8px;
  background-color: #fff;
  color: #f5811f;
  font-weight: 700;
  font-size: var(--font-size-label);
`;

const EmptySetting = styled.div`
  display: flex;
  min-height: 100px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const EmptyDescription = styled.div`
  color: #888;
  font-size: var(--font-size-15);
`;

const SettingLink = styled(Link)`
  display: inline-flex;
  height: 36px;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-14);
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background-color: #ffe8d2;
  }
`;

const DeleteCard = styled.div`
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border: 1px solid #efcaca;
  border-radius: 10px;
  background-color: #fff3f3;

  button {
    width: auto;
    min-width: 108px;
    height: 38px;
    padding: 0 16px;
    border: 1px solid #b83f3f;
    border-radius: 7px;
    background: #c94f4f;
    color: #fff;
    font-size: var(--font-size-13);
    font-weight: 800;
    box-shadow: 0 4px 10px rgba(180, 55, 55, 0.18);
  }

  button:hover:not(:disabled) {
    border-color: #a83232;
    background: #ae3838;
    color: #fff;
  }

  @media (max-width: 760px) {
    grid-template-columns: 42px minmax(0, 1fr);
  }
`;

const DeleteWarningIcon = styled.span`
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #c94f4f;
  color: #fff;
  font-size: var(--font-size-21);
  font-weight: 900;
`;

const CreativeTypeValue = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #333;
  font-size: var(--font-size-body);
  font-weight: 700;
`;

const CreativeIcon = styled.span`
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: ${({$type}) =>
      $type === "AUDIO" ? "#fff1e6" :
          $type === "BANNER" ? "#eef5ff" : "#eef9f3"};
  color: ${({$type}) =>
      $type === "AUDIO" ? "#e76f12" :
          $type === "BANNER" ? "#3977c5" : "#258652"};

  svg {
    width: 20px;
    height: 20px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const DeleteWarningContent = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
`;

const DeleteWarningHeadline = styled.div`
  color: #a73d3d;
  font-size: var(--font-size-14);
  font-weight: 800;
`;

const DeleteWarningText = styled.div`
  color: #7f5c5c;
  font-size: var(--font-size-12);
  line-height: 1.55;
`;

const DeleteCampaignName = styled.div`
  color: #a73d3d;
  font-size: var(--font-size-12);
  font-weight: 800;
`;

const DeleteDescription = styled.div`
  color: #9a6a6a;
  font-size: var(--font-size-11);
  line-height: 1.5;
`;

const DeleteActionArea = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 760px) {
    grid-column: 1 / -1;
    align-items: stretch;

    button {
      width: 100%;
    }
  }
`;

const SaveButton = styled.button`
  min-width: 84px;
  height: 38px;
  padding: 0 18px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-15);
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;
const BasicInfoGrid = styled.div`
  //display: grid;
  //grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
  padding: 9px 14px;
  background-color: #f5f7f9;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    padding: 12px 15px;
  }
`;

const BasicInfoItem = styled.div`
  display: flex;
  min-width: 0;
  min-height: 46px;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;

  &:nth-last-child(-n + 2) {
    border-bottom: 0;
  }

  @media (max-width: 760px) {
    &:nth-last-child(-n + 2) {
      border-bottom: 1px solid #f0f0f0;
    }

    &:last-child {
      border-bottom: 0;
    }
  }
`;

const BasicInfoLabel = styled.div`
  width: 92px;
  min-width: 92px;
  color: #555;
  font-size: var(--font-size-12);
  font-weight: 700;
`;

const BasicInfoContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

const BasicInfoValue = styled.div`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: #333;
  font-size: var(--font-size-12);
  font-weight: 500;
  line-height: 32px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BasicInfoInput = styled(Input)`
  width: auto;
  min-width: 0;
  height: 32px;
  flex: 1;
  border: 1px solid #dfe3e6;
  border-radius: 6px;
  background-color: #fff;
  color: #333;
  font-size: var(--font-size-12);
  font-weight: 500;

  &:focus {
    border-color: #f5811f;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const CampaignNameEditArea = styled.div`
  display: flex;
  min-width: 0;
  min-height: 32px;
  flex: 1;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const CampaignNameActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
`;

const EditNameButton = styled.button`
  min-width: 44px;
  height: 30px;
  flex: 0 0 auto;
  margin-left: auto;
  padding: 0 8px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-11);
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const SaveNameButton = styled.button`
  min-width: 44px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-11);
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const CancelEditButton = styled.button`
  min-width: 44px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background-color: #fff;
  color: #666;
  font-size: var(--font-size-11);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #f5811f;
    background-color: #fffaf6;
    color: #f5811f;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;
const DemographicSection = styled.div`
  margin-top: 18px;
`;

const DemographicSectionTitle = styled.div`
  margin-bottom: 12px;
  color: #444;
  font-size: var(--font-size-15);
  font-weight: 700;
`;

const TargetTitleRow = styled.div`
  display: flex;
  min-height: 26px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;

  ${TargetTitle} {
    margin-bottom: 0;
  }
`;

const TargetTagsRow = styled.div`
  justify-items: end;
`;

const TargetTypeBadge = styled.span`
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  padding: 0 9px;
  border: 1px solid
    ${({$exclude}) =>
    $exclude
        ? "#ffc9c9"
        : "#f1d4c2"};
  border-radius: 12px;
  background-color:
    ${({$exclude}) =>
    $exclude
        ? "#fff5f5"
        : "#fff4e8"};
  color:
    ${({$exclude}) =>
    $exclude
        ? "#e03131"
        : "#f5811f"};
  font-size: var(--font-size-12);
  font-weight: 600;
`;

const EmptyTargetText = styled.span`
  color: #f5811f;
  font-weight: 600;
  font-size: var(--font-size-body);
`;

const EmptyDemographicTarget = styled.div`
  display: flex;
  min-height: 70px;
  align-items: center;
  justify-content: center;
  border: 1px dashed #ddd;
  border-radius: 8px;
  background-color: #fff;
  color: #999;
  font-size: var(--font-size-14);
`;
